// The authority: every game in the catalogue, every live room, every connected player, and the
// tick loop that drives them. Transport-agnostic: it never touches a socket library. A host
// adapts its sockets to the two small interfaces below and hands HTTP API requests to `api()`.
// Two hosts exist: Node + ws (server.ts) and a Cloudflare Durable Object (../edge/worker.ts).
//
// Nothing here is Node-specific: timers and performance.now() are globals everywhere this
// runs, and ids come from Web Crypto. Keep it that way; the browser is a possible third host.

import { loadModule, type GameModule } from "../contract.js";
import type { GameEntry } from "../../games/index.js";
import { Room, ClientView } from "./room.js";
import { parseClientMsg, type ServerMsg } from "../shared/protocol.js";
import { searchSprites } from "../shared/sprites.js";
import { spriteIndex } from "../shared/sprite-index.js";

/** How long a disconnected player keeps their entity before onLeave runs. */
export const GRACE_MS = 3000;
/** How long a fresh socket has to say hello. */
export const HELLO_TIMEOUT_MS = 5000;
const METRICS_EVERY_MS = 5000;
const MAX_CATCHUP_TICKS = 5;

/** What the authority needs from a socket. */
export interface Socket {
  send(text: string): void;
  close(code: number, reason: string): void;
  /** True while sends will be delivered. */
  readonly open: boolean;
}

/** What a host feeds into the authority from one socket. */
export interface Connection {
  /** A frame from the client: string, ArrayBuffer or a view on one. */
  message(data: unknown): void;
  /** The socket closed or errored. Idempotent. */
  gone(): void;
}

/** A loaded catalogue entry. */
export interface GameSource {
  slug: string;
  blurb: string;
  module: GameModule;
}

/** Feed counters. In memory only: they reset when the host does (no persistence yet). */
export interface Social {
  plays: number;
  likes: number;
  saves: number;
  shares: number;
  reports: number;
}

export interface Game extends GameSource {
  social: Social;
}

export interface GameSummary {
  slug: string;
  name: string;
  blurb: string;
  players: number;
  rooms: number;
  max: number;
  plays: number;
  likes: number;
  saves: number;
  shares: number;
}

export interface RoomInfo {
  id: string;
  game: string;
  players: number;
  max: number;
  tick: number;
}

/** A plain HTTP reply; the host adds CORS and sends it. */
export interface ApiResponse {
  status: number;
  type: string;
  body: string;
}

type Timer = ReturnType<typeof setTimeout>;

interface Client {
  socket: Socket | null;
  playerId: string;
  token: string;
  name: string;
  view: ClientView;
  live: LiveRoom;
  grace?: Timer;
  /** Bytes sent at the last metrics report. */
  bytesAtReport: number;
}

class LiveRoom {
  readonly game: Game;
  readonly room: Room;
  readonly clients = new Map<string, Client>();
  private timer: Timer | null = null;
  private next = 0;
  private readonly intervalMs: number;
  // Metrics accumulated between reports.
  private stepSamples: number[] = [];
  private snapSamples: number[] = [];
  private lastReport: number;
  dead = false;

  constructor(
    private readonly authority: Authority,
    game: Game,
    id: string,
    seed: number,
  ) {
    this.game = game;
    this.room = new Room(game.module, { id, seed });
    this.intervalMs = 1000 / game.module.meta.tickRate;
    this.lastReport = authority.now();
  }

  get id(): string {
    return this.room.id;
  }

  start(): void {
    if (this.timer) return;
    this.next = performance.now();
    this.schedule();
  }

  stop(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
  }

  private schedule(): void {
    const delay = Math.max(0, this.next - performance.now());
    this.timer = setTimeout(() => this.onTimer(), delay);
  }

  private onTimer(): void {
    this.timer = null;
    if (this.dead) return;
    const now = performance.now();
    let n = 0;
    while (this.next <= now && n < MAX_CATCHUP_TICKS) {
      this.tick();
      if (this.dead) return;
      this.next += this.intervalMs;
      n++;
    }
    if (this.next <= now) this.next = now + this.intervalMs; // hopelessly behind: drop ticks rather than spiral
    this.schedule();
  }

  private tick(): void {
    try {
      this.room.step();
    } catch (err) {
      // A broken module breaks its own room and nothing else.
      console.error(`[room ${this.id}] module threw during tick ${this.room.state.tick}:`, err);
      this.die(`game error: ${(err as Error).message ?? String(err)}`);
      return;
    }
    this.stepSamples.push(this.room.lastStepMs);

    const t0 = performance.now();
    const time = this.authority.now();
    for (const c of this.clients.values()) {
      if (!c.socket || !c.socket.open) continue;
      const snap = this.room.snapshot(c.playerId, c.view, time);
      const json = JSON.stringify(snap);
      c.view.bytes += json.length;
      c.socket.send(json);
    }
    this.snapSamples.push(performance.now() - t0);

    if (time - this.lastReport >= METRICS_EVERY_MS) this.report(time);
  }

  private report(time: number): void {
    const secs = (time - this.lastReport) / 1000;
    this.lastReport = time;
    const avg = (a: number[]) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : 0);
    const max = (a: number[]) => (a.length ? Math.max(...a) : 0);
    let bytes = 0;
    let n = 0;
    for (const c of this.clients.values()) {
      bytes += c.view.bytes - c.bytesAtReport;
      c.bytesAtReport = c.view.bytes;
      n++;
    }
    const perClient = n ? bytes / secs / n / 1024 : 0;
    console.log(
      `[room ${this.id}] players=${this.room.playerCount} entities=${this.room.liveEntities().length} ` +
        `step avg=${avg(this.stepSamples).toFixed(2)}ms max=${max(this.stepSamples).toFixed(2)}ms ` +
        `snapshots avg=${avg(this.snapSamples).toFixed(2)}ms ` +
        `out=${perClient.toFixed(1)} KB/s/client state=${(this.room.stateBytes() / 1024).toFixed(0)} KB`,
    );
    this.stepSamples = [];
    this.snapSamples = [];
  }

  send(c: Client, msg: ServerMsg): void {
    if (c.socket && c.socket.open) c.socket.send(JSON.stringify(msg));
  }

  /** Tell everyone, close every socket, and forget the room. Never throws: this runs inside the tick. */
  die(reason: string, code = 1011): void {
    this.dead = true;
    this.stop();
    for (const c of this.clients.values()) {
      try {
        this.send(c, { t: "err", msg: reason });
        c.socket?.close(code, reason.slice(0, 120));
      } catch (err) {
        console.error(`[room ${this.id}] could not close ${c.name}'s socket:`, err);
      }
      if (c.grace) clearTimeout(c.grace);
    }
    this.clients.clear();
    this.authority.forget(this);
  }
}

const SOCIAL_ACTIONS = new Set(["like", "unlike", "save", "unsave", "share", "report"]);

export class Authority {
  readonly games = new Map<string, Game>();
  /** Live rooms keyed by "<game>/<room>". */
  private readonly rooms = new Map<string, LiveRoom>();
  /** Disconnected clients waiting out their grace window, by resume token. */
  private readonly resumable = new Map<string, Client>();
  private readonly started = performance.now();

  constructor(games: Iterable<GameSource>) {
    for (const g of games) {
      this.games.set(g.slug, { ...g, social: { plays: 0, likes: 0, saves: 0, shares: 0, reports: 0 } });
    }
    if (this.games.size === 0) throw new Error("no games in the catalogue");
  }

  /** Load every module in a catalogue (games/index.ts) and build the authority over them. */
  static async load(catalogue: Record<string, GameEntry>): Promise<Authority> {
    const list: GameSource[] = [];
    for (const [slug, entry] of Object.entries(catalogue)) {
      list.push({ slug, blurb: entry.blurb, module: await loadModule(entry.load) });
    }
    return new Authority(list);
  }

  /** Server clock: milliseconds since this authority started. Clients sync to this; nobody compares Date.now(). */
  now(): number {
    return Math.round((performance.now() - this.started) * 10) / 10;
  }

  /** One line naming the games, for a host's startup log. */
  describe(): string {
    return [...this.games.values()].map((g) => `${g.slug} ("${g.module.meta.name}")`).join(", ");
  }

  // ---- sockets --------------------------------------------------------------------------------

  /**
   * Take over a freshly opened socket. `gameParam` and `roomParam` are the raw query values
   * (null when absent): an unknown game closes the socket at once with 4004.
   */
  connect(socket: Socket, gameParam: string | null, roomParam: string | null): Connection {
    const firstGame = this.games.keys().next().value as string;
    const game = this.games.get(slugify(gameParam, firstGame));
    if (!game) {
      socket.close(4004, "unknown game");
      return { message() {}, gone() {} };
    }
    const slug = slugify(roomParam, "main");
    let client: Client | null = null;
    let done = false;

    const helloTimer = setTimeout(() => {
      if (!client) socket.close(4000, "no hello");
    }, HELLO_TIMEOUT_MS);

    const message = (data: unknown): void => {
      if (done) return;
      const msg = parseClientMsg(data);
      if (!msg) return;
      if (msg.t === "hello") {
        if (client) return;
        clearTimeout(helloTimer);
        const resumed = msg.resume ? this.resumable.get(msg.resume) : undefined;
        if (resumed && !resumed.live.dead && resumed.live.game === game) {
          this.resumable.delete(resumed.token);
          if (resumed.grace) clearTimeout(resumed.grace);
          resumed.grace = undefined;
          resumed.socket = socket;
          client = resumed;
          // The client starts from nothing: forget what the old socket was told.
          client.view = new ClientView();
          console.log(`[room ${client.live.id}] ${client.name} resumed`);
        } else {
          const live = this.roomFor(game, slug);
          const playerId = randomHex(4);
          const name = (msg.name ?? "").trim() || `guest-${playerId.slice(0, 4)}`;
          client = { socket, playerId, token: randomHex(16), name, view: new ClientView(), live, bytesAtReport: 0 };
          try {
            live.room.join(playerId, name);
          } catch (err) {
            socket.close(4001, (err as Error).message);
            return;
          }
          live.clients.set(playerId, client);
          live.start();
          game.social.plays++;
          console.log(`[room ${live.id}] ${name} joined (${live.room.playerCount} playing)`);
        }
        client.live.send(client, {
          t: "welcome",
          id: client.playerId,
          token: client.token,
          game: game.slug,
          room: client.live.id,
          meta: game.module.meta,
          schema: game.module.inputSchema,
          time: this.now(),
        });
        return;
      }
      if (!client) return;
      if (msg.t === "in") client.live.room.setInput(client.playerId, msg.i);
      else if (msg.t === "ping") client.live.send(client, { t: "pong", c: msg.c, s: this.now() });
    };

    const gone = (): void => {
      if (done) return;
      done = true;
      clearTimeout(helloTimer);
      const c = client;
      if (!c || c.socket !== socket) return; // a later socket already resumed this player
      c.socket = null;
      const live = c.live;
      live.room.clearInput(c.playerId); // neutral input while away: no stuck keys walking into hazards
      this.resumable.set(c.token, c);
      c.grace = setTimeout(() => {
        this.resumable.delete(c.token);
        live.clients.delete(c.playerId);
        live.room.leave(c.playerId);
        console.log(`[room ${live.id}] ${c.name} left (${live.room.playerCount} playing)`);
        this.sleepIfEmpty(live);
      }, GRACE_MS);
    };

    return { message, gone };
  }

  // ---- rooms ----------------------------------------------------------------------------------

  /** Find a room with space for the slug, overflowing into numbered shards. */
  private roomFor(game: Game, slug: string): LiveRoom {
    for (let i = 1; i < 1000; i++) {
      const id = `${game.slug}/${i === 1 ? slug : `${slug}-${i}`}`;
      let live = this.rooms.get(id);
      if (!live) {
        live = new LiveRoom(this, game, id, hashSeed(id));
        this.rooms.set(id, live);
        console.log(`[room ${id}] woke`);
      }
      if (live.room.playerCount < game.module.meta.players.max) return live;
    }
    throw new Error("no room available");
  }

  private sleepIfEmpty(live: LiveRoom): void {
    if (live.clients.size > 0 || live.dead) return;
    live.stop();
    this.rooms.delete(live.id);
    console.log(`[room ${live.id}] slept (world reset)`);
  }

  /** @internal A room that died removes itself. */
  forget(live: LiveRoom): void {
    this.rooms.delete(live.id);
  }

  liveRooms(): RoomInfo[] {
    return [...this.rooms.values()].map((l) => ({
      id: l.id,
      game: l.game.slug,
      players: l.room.playerCount,
      max: l.game.module.meta.players.max,
      tick: l.room.state.tick,
    }));
  }

  /** Stop every room and close every socket (tests, shutdown). */
  close(): void {
    for (const live of [...this.rooms.values()]) live.die("server closing", 1001);
    for (const c of this.resumable.values()) if (c.grace) clearTimeout(c.grace);
    this.resumable.clear();
  }

  // ---- http api -------------------------------------------------------------------------------

  summary(g: Game): GameSummary {
    let players = 0;
    let live = 0;
    for (const r of this.rooms.values()) {
      if (r.game !== g) continue;
      players += r.room.playerCount;
      live++;
    }
    return {
      slug: g.slug,
      name: g.module.meta.name,
      blurb: g.blurb,
      players,
      rooms: live,
      max: g.module.meta.players.max,
      plays: g.social.plays,
      likes: g.social.likes,
      saves: g.social.saves,
      shares: g.social.shares,
    };
  }

  /**
   * The HTTP API, independent of any server library. `remote` is the caller's address, for the
   * report log only.
   *
   *   GET  /api/games                                   the catalogue with live counts (the Play feed)
   *   POST /api/games/<slug>/<like|unlike|save|unsave|share|report>
   *   GET  /api/rooms                                   live rooms
   *   GET  /api/sprites?q=knight&style=pixel&limit=20   keyword search of the sprite library; no q lists the packs
   *   GET  /api/health                                  "ok"
   */
  api(method: string, pathname: string, remote?: string, query?: URLSearchParams): ApiResponse {
    const json = (body: unknown): ApiResponse => ({ status: 200, type: "application/json", body: JSON.stringify(body) });
    const notFound: ApiResponse = { status: 404, type: "text/plain", body: "not found" };
    if (pathname === "/api/sprites") {
      const q = query?.get("q")?.trim() ?? "";
      if (!q) return json({ packs: spriteIndex.packs, count: spriteIndex.sprites.length });
      const limit = Math.max(1, Math.min(50, Number(query?.get("limit")) || 20));
      const style = query?.get("style") || undefined;
      const pack = query?.get("pack") || undefined;
      const sprites = searchSprites(spriteIndex, q, { style, pack, limit }).map((s) => ({ ...s, url: `/sprites/${s.sheet}` }));
      return json({ q, style: style ?? null, sprites });
    }
    if (pathname === "/api/games") {
      return json({ games: [...this.games.values()].map((g) => this.summary(g)), time: this.now() });
    }
    const social = /^\/api\/games\/([a-z0-9-]{1,32})\/([a-z]+)$/.exec(pathname);
    if (social && method === "POST") {
      const game = this.games.get(social[1]);
      const action = social[2];
      if (!game || !SOCIAL_ACTIONS.has(action)) return notFound;
      const s = game.social;
      if (action === "like") s.likes++;
      else if (action === "unlike") s.likes = Math.max(0, s.likes - 1);
      else if (action === "save") s.saves++;
      else if (action === "unsave") s.saves = Math.max(0, s.saves - 1);
      else if (action === "share") s.shares++;
      else if (action === "report") {
        s.reports++;
        // Report button from day one (GOTCHAS). Until there is a review queue, the log is the queue.
        console.warn(`[report] ${game.slug} reported from ${remote ?? "unknown"} (${s.reports} so far)`);
      }
      return json(this.summary(game));
    }
    if (pathname === "/api/rooms") return json({ rooms: this.liveRooms(), time: this.now() });
    if (pathname === "/api/health") return { status: 200, type: "text/plain", body: "ok" };
    return notFound;
  }
}

// ---- helpers ----------------------------------------------------------------------------------

function slugify(s: string | null, fallback: string): string {
  const slug = (s ?? "").toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, 32);
  return slug || fallback;
}

function hashSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}

/** `n` random bytes as lower-case hex, from Web Crypto (a global in Node 19+, Workers and browsers). */
function randomHex(n: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(n));
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}
